import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Printer, RotateCw } from 'lucide-react'
import { api } from '../../lib/api'
import { interval } from '../../lib/perioade'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { DateRangeField } from '../../components/ui/DateField'
import { StareLista } from '../../components/ui/StareLista'

/*
  Operațiuni → Expedieri AWB (task #2482, 03.11.2026). Coletele cu AWB emis din CRM — cererile de
  marfă trimise prin curier —, pentru depozit: cui pleacă, unde a ajuns și AWB-ul de printat.
*/

type Stare = 'de_predat' | 'in_drum' | 'livrat' | 'problema'
const ETICHETA_STARE: Record<Stare, string> = {
  de_predat: 'Nepreluat de curier',
  in_drum: 'În drum',
  livrat: 'Livrat',
  problema: 'De urmărit',
}
const CULOARE_STARE: Record<Stare, string> = {
  de_predat: 'bg-b4l-amber-light text-b4l-amber-dark',
  in_drum: 'bg-b4l-blue-light text-b4l-blue-dark',
  livrat: 'bg-b4l-green-light text-b4l-green-dark',
  problema: 'bg-b4l-red-light text-b4l-red-dark',
}

interface Expediere {
  cerereId: number
  awb: string
  curier: string
  emisLa: string
  emisDe: string | null
  printatLa: string | null
  destinatar: string
  localitate: string
  activitate: string
  produse: { denumire: string; cantitate: number }[]
  stare: Stare
  ultimaMiscare: string | null
}
interface Raspuns {
  expedieri: Expediere[]
  peStare: Record<Stare, number>
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const dataOra = (s: string) =>
  new Date(s).toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Mesajul de eroare din raspunsul API, si cand raspunsul a fost cerut ca Blob. */
async function mesajEroare(e: unknown): Promise<string | null> {
  const data = (e as { response?: { data?: unknown } })?.response?.data
  try {
    const j = data instanceof Blob ? JSON.parse(await data.text()) : data
    return (j as { error?: { message?: string } } | undefined)?.error?.message ?? null
  } catch {
    return null
  }
}

export function ExpedieriAwbPage() {
  const [param, setParam] = useSearchParams()
  const qc = useQueryClient()
  const scurtaturi = useMemo(() => {
    const azi = new Date()
    const de = new Date(azi)
    de.setDate(de.getDate() - 13)
    return [
      { cheie: '14z', eticheta: 'Ultimele 14 zile', from: iso(de), to: iso(azi) },
      { cheie: 'lc', eticheta: 'Luna curentă', ...interval('luna_curenta') },
      { cheie: 'lp', eticheta: 'Luna precedentă', ...interval('luna_precedenta') },
    ]
  }, [])
  const deLa = param.get('de_la') ?? scurtaturi[0].from
  const panaLa = param.get('pana_la') ?? scurtaturi[0].to
  const [stare, setStare] = useState<'toate' | Stare>('toate')
  const [cauta, setCauta] = useState('')
  /** Randul care se printeaza acum, si cele la care FAN n-a dat PDF-ul. */
  const [printeaza, setPrinteaza] = useState<number | null>(null)
  const [esuate, setEsuate] = useState<Set<number>>(new Set())

  const seteaza = (de: string, pana: string) => {
    const p = new URLSearchParams(param)
    p.set('de_la', de)
    p.set('pana_la', pana)
    setParam(p, { replace: true })
  }

  const q = useQuery<Raspuns>({
    queryKey: ['expedieri-awb', deLa, panaLa, stare, cauta],
    queryFn: async () =>
      (
        await api.get('/expedieri-awb', {
          params: {
            de_la: deLa,
            pana_la: panaLa,
            ...(stare !== 'toate' ? { stare } : {}),
            ...(cauta.trim() ? { q: cauta.trim() } : {}),
          },
        })
      ).data,
  })

  const printeazaAwb = async (r: Expediere) => {
    /*
      Fereastra se deschide INAINTE de cerere, pe clic: deschisa dupa `await`, browserul o
      blocheaza ca pop-up. Primeste PDF-ul cand vine.
    */
    const fereastra = window.open('', '_blank')
    setPrinteaza(r.cerereId)
    try {
      const resp = await api.get(`/expedieri-awb/${r.cerereId}/awb.pdf`, {
        responseType: 'blob',
        timeout: 60_000,
      })
      const url = URL.createObjectURL(resp.data as Blob)
      if (fereastra) fereastra.location.href = url
      else window.location.assign(url)
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setEsuate((s) => {
        const n = new Set(s)
        n.delete(r.cerereId)
        return n
      })
      void qc.invalidateQueries({ queryKey: ['expedieri-awb'] })
    } catch (e) {
      fereastra?.close()
      setEsuate((s) => new Set(s).add(r.cerereId))
      toast.error((await mesajEroare(e)) ?? `AWB-ul ${r.awb} nu s-a putut deschide. Reîncearcă.`)
    } finally {
      setPrinteaza(null)
    }
  }

  const rand = q.data?.expedieri ?? []
  const ps = q.data?.peStare ?? { de_predat: 0, in_drum: 0, livrat: 0, problema: 0 }
  const total = ps.de_predat + ps.in_drum + ps.livrat + ps.problema

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Expedieri AWB"
        descriere="Coletele cu AWB emis din CRM (cererile de marfă trimise prin curier). De aici se printează AWB-ul."
      />

      <div className="flex flex-wrap items-center gap-2 text-corp">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Perioada">
          {scurtaturi.map((sc) => (
            <button
              key={sc.cheie}
              type="button"
              onClick={() => seteaza(sc.from, sc.to)}
              className={`rounded-caseta border px-3 py-1.5 ${
                sc.from === deLa && sc.to === panaLa
                  ? 'border-b4l-blue bg-b4l-blue font-medium text-white'
                  : 'border-b4l-border bg-b4l-surface text-b4l-text hover:border-b4l-blue'
              }`}
            >
              {sc.eticheta}
            </button>
          ))}
        </div>
        <div className="w-full sm:w-72">
          <DateRangeField
            fromValue={deLa}
            toValue={panaLa}
            onChange={(a, b) => a && b && seteaza(a, b)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <ChipuriFiltru<'toate' | Stare>
          eticheta="Stare"
          ales={stare}
          onAlege={setStare}
          chipuri={[
            { cheie: 'toate', eticheta: 'Toate', numar: total },
            { cheie: 'de_predat', eticheta: ETICHETA_STARE.de_predat, numar: ps.de_predat },
            { cheie: 'in_drum', eticheta: ETICHETA_STARE.in_drum, numar: ps.in_drum },
            { cheie: 'livrat', eticheta: ETICHETA_STARE.livrat, numar: ps.livrat },
            { cheie: 'problema', eticheta: ETICHETA_STARE.problema, numar: ps.problema },
          ]}
        />
        <input
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
          placeholder="Caută AWB, destinatar, nr. cerere, activitate"
          aria-label="Caută în expedieri"
          className="min-w-[260px] flex-1 rounded-caseta border border-b4l-border bg-b4l-surface px-3 py-1.5 text-corp"
        />
      </div>

      <StareLista
        interogare={q}
        gol={rand.length === 0}
        mesajGol="Niciun colet cu AWB din CRM în perioada aleasă."
      >
        <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
          <table className="w-full min-w-[900px] border-collapse text-corp">
            <thead>
              <tr className="text-left text-mic text-b4l-text-muted">
                <th className="border-b border-b4l-border px-3.5 py-2.5 font-medium">Emis</th>
                <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Destinatar</th>
                <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Cererea</th>
                <th className="border-b border-b4l-border px-2 py-2.5 font-medium">AWB</th>
                <th className="border-b border-b4l-border px-2 py-2.5 font-medium">Stare</th>
                <th className="border-b border-b4l-border px-3.5 py-2.5 text-right font-medium">
                  <span className="sr-only">Printare</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rand.map((r) => (
                <tr key={r.cerereId} className="align-top">
                  <td className="border-b border-b4l-border px-3.5 py-2 tabular-nums">
                    {dataOra(r.emisLa)}
                    {r.emisDe && <div className="text-mic text-b4l-text-muted">{r.emisDe}</div>}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    <div className="font-medium">{r.destinatar}</div>
                    {r.localitate && (
                      <div className="text-mic text-b4l-text-muted">{r.localitate}</div>
                    )}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    <div>Cererea #{r.cerereId}</div>
                    <div className="text-mic text-b4l-text-muted">{r.activitate}</div>
                    {r.produse.length > 0 && (
                      <div className="text-mic text-b4l-text-dim">
                        {r.produse.map((p) => `${p.denumire} ×${p.cantitate}`).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    <div className="font-medium tabular-nums">{r.awb}</div>
                    <div className="text-mic text-b4l-text-muted">{r.curier}</div>
                  </td>
                  <td className="border-b border-b4l-border px-2 py-2">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-mic font-medium ${CULOARE_STARE[r.stare]}`}
                    >
                      {ETICHETA_STARE[r.stare]}
                    </span>
                    {r.ultimaMiscare && r.stare !== 'livrat' && (
                      <div className="mt-0.5 text-mic text-b4l-text-muted">{r.ultimaMiscare}</div>
                    )}
                  </td>
                  <td className="border-b border-b4l-border px-3.5 py-2 text-right">
                    <Button
                      variant={r.printatLa ? 'secondary' : 'primary'}
                      onClick={() => void printeazaAwb(r)}
                      loading={printeaza === r.cerereId}
                      disabled={printeaza != null && printeaza !== r.cerereId}
                    >
                      {esuate.has(r.cerereId) ? <RotateCw size={14} /> : <Printer size={14} />}
                      {esuate.has(r.cerereId) ? 'Reîncearcă' : 'Printează AWB-ul'}
                    </Button>
                    {r.printatLa && (
                      <div className="mt-1 text-mic text-b4l-text-muted">
                        AWB printat {dataOra(r.printatLa)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="m-0 px-3.5 py-2.5 text-mic text-b4l-text-dim">
            Doar AWB-urile emise din CRM; comenzile din ERP au AWB-ul acolo. Starea vine din
            borderoul FAN, adus o dată pe zi — un colet emis azi apare „Nepreluat de curier” până
            atunci. Dacă PDF-ul nu e încă la FAN, butonul devine „Reîncearcă”.
          </p>
        </div>
      </StareLista>
    </div>
  )
}
